/* global window */

export const FOCUS_STRATEGY_CRITICAL_ACTION = 'criticalAction';
export const FOCUS_STRATEGY_LEGAL_CONTENT = 'legalContent';
export const FOCUS_STRATEGY_LONG_CONTENT = 'longContent';
export const FOCUS_STRATEGY_FORM = 'form';
export const FOCUS_STRATEGY_DISMISS = 'dismiss';

const CRITICAL_KEYWORDS = [
    'löschen', 'entfernen', 'kündigen', 'deaktivieren', 'abbuchen', 'stornieren',
    'unwiderruflich', 'irreversibel', 'permanent', 'endgültig', 'final', 'einrichten',
    'kostenpflichtig', 'bezahlen',
];

const CANCEL_KEYWORDS = [
  'abbrechen',
];

const LEGAL_KEYWORDS = [
    'cookie', 'tracking', 'datenschutz', 'privatsphäre', 'rechtlich', 'legal',
    'nutzungsbedingungen', 'agb', 'impressum', 'widerrufsrecht', 'recht',
];

const DISMISS_KEYWORDS = [
    'startseite', 'weiter', 'alles klar', 'schließen', 'schliessen', 'fortfahren', 'ok', 'okay',
    'bestätigen', 'ja', 'jetzt',
];

const UI_OVERLAY_WRAPPER_SELECTOR = 'div.UIOverlayWrapper';

const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6, [role="heading"]';

const BUTTON_SELECTOR = 'button, [role="button"], input[type="submit"], input[type="button"]';
const BUTTON_CLOSE_ICON_CLASS = 'ButtonCloseIcon';
const BUTTON_CLOSE_ICON_SELECTOR = `button.${BUTTON_CLOSE_ICON_CLASS}`;

const FORM_ELEMENT_SELECTOR = 'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])';

const getCurrentActiveElement = () => window.document.activeElement;

const elementIsNaturallyFocusable = (element) => {
    if (element.disabled) return false;

    switch (element.tagName.toLowerCase()) {
        case 'a':
        case 'area':
            return element.hasAttribute('href');
        case 'input':
        case 'select':
        case 'textarea':
        case 'button':
            return !element.disabled;
        case 'iframe':
        case 'object':
        case 'embed':
            return true;
        default:
            // Check for contenteditable or positive tabindex
            return element.isContentEditable
                || (element.hasAttribute('tabindex')
                    && parseInt(element.getAttribute('tabindex'), 10) >= 0);
    }
};

const getDialogTitle = (container) => {
  const heading = container.querySelector(HEADING_SELECTOR);

  if (heading) {
    // Make it focusable if it isn't already
    if (!elementIsNaturallyFocusable(heading)) {
      heading.setAttribute('tabindex', '-1');
    }

    return heading;
  }

  return null;
};

const checkForCriticalActions = (container) => {
    const buttons = container.querySelectorAll(BUTTON_SELECTOR);

    return Array.from(buttons).some(button => {
        const text = (button.textContent || button.value || '').toLowerCase();
        return CRITICAL_KEYWORDS.some(keyword => text.includes(keyword));
    });
};

const checkForLegalKeywords = (container) => {
    const text = container.textContent.toLowerCase();
    const foundKeywords = LEGAL_KEYWORDS.filter(keyword => text.includes(keyword));

    return foundKeywords.length > 0;
};

const checkForLongContentByHeight = (container) => {
    // Find the actual dialog content (UIOverlayWrapper)
    const dialogContent = container.querySelector(UI_OVERLAY_WRAPPER_SELECTOR);
    if (!dialogContent) {
        return false;
    }

    // Get dialog content height
    let totalHeight = dialogContent.scrollHeight;

    // Add close button height if present
    const closeButton = container.querySelector(BUTTON_CLOSE_ICON_SELECTOR);
    if (closeButton) {
        const closeButtonHeight = closeButton.offsetHeight;
        totalHeight += closeButtonHeight;
    }

    const viewportHeight = window.innerHeight;

    if (totalHeight > viewportHeight * 0.8) {
        return true;
    }

    return false;
};

const getFirstFormElement = (container) => {
    const formElements = container.querySelectorAll(FORM_ELEMENT_SELECTOR);
    return formElements.length > 0 ? formElements[0] : null;
};

const getCancelButton = (container) => {
    const buttons = container.querySelectorAll(BUTTON_SELECTOR);

    const element = Array.from(buttons).find(button => {
        // Skip close button
        if (button.classList.contains(BUTTON_CLOSE_ICON_CLASS)) {
            return false;
        }

        const text = (button.textContent || button.value || '').toLowerCase();
        const ariaLabel = (button.getAttribute('aria-label') || '').toLowerCase();

        return CANCEL_KEYWORDS.some(keyword =>
            text.includes(keyword) || ariaLabel.includes(keyword));
    }) || null;

    return element;
};

const getDismissButton = (container) => {
    const buttons = container.querySelectorAll(BUTTON_SELECTOR);

    const element = Array.from(buttons).find(button => {
        // Skip close button
        if (button.classList.contains(BUTTON_CLOSE_ICON_CLASS)) {
            return false;
        }

        const text = (button.textContent || button.value || '').toLowerCase();
        const ariaLabel = (button.getAttribute('aria-label') || '').toLowerCase();

        return DISMISS_KEYWORDS.some(keyword =>
            text.includes(keyword) || ariaLabel.includes(keyword));
    }) || null;

    return element;
};

const getSafestActionButton = (container) => {
    // First try to get cancel button
    const cancelButton = getCancelButton(container);
    if (cancelButton) return cancelButton;

    // If no cancel button, get the button that's NOT destructive
    const buttons = container.querySelectorAll(BUTTON_SELECTOR);

    return Array.from(buttons).find(button => {
        const text = (button.textContent || button.value || '').toLowerCase();
        return !CRITICAL_KEYWORDS.some(keyword => text.includes(keyword));
    }) || null;
};

const getCloseButton = (container) => container.querySelector(BUTTON_CLOSE_ICON_SELECTOR);

function getFirstElementToFocusByStrategy(dialogContainer, focusStrategy = '') {
    if (focusStrategy) {
        switch (focusStrategy) {
            case FOCUS_STRATEGY_CRITICAL_ACTION:
                console.debug('focusStrategy', `explicit -> ${focusStrategy}`);
                return getSafestActionButton(dialogContainer);
            case FOCUS_STRATEGY_LEGAL_CONTENT:
            case FOCUS_STRATEGY_LONG_CONTENT:
                console.debug('focusStrategy', `explicit -> ${focusStrategy}`);
                return getDialogTitle(dialogContainer);
            case FOCUS_STRATEGY_FORM:
                console.debug('focusStrategy', `explicit -> ${focusStrategy}`);
                return getFirstFormElement(dialogContainer);
            case FOCUS_STRATEGY_DISMISS:
                console.debug('focusStrategy', `explicit -> ${focusStrategy}`);
                return getDismissButton(dialogContainer);
            default:
                console.warn('Unknown focusStrategy:', focusStrategy);
        }
    }

    return null;
}

export const focusElement = (element, force = false) => {
    // check if element is a valid element
    if (!element) {
        return null;
    }

    // get current active element
    const currentActiveElement = getCurrentActiveElement();

    // if current active element is the same as the element, return
    if (currentActiveElement === element) {
        return currentActiveElement;
    }

    // check if element is naturally focusable
    const naturallyFocusable = elementIsNaturallyFocusable(element);

    // focus element if it is naturally focusable or force is true
    if (naturallyFocusable || force) {

        // prepare element for focus
        if (!naturallyFocusable && force) {
            element.setAttribute('tabindex', '-1');
        }

        try {
            element.focus();
            console.debug('Successfully focused element:', element);
        } catch (error) {
            console.error('Error focusing element:', error, element);
        }
    } else {
        console.warn('element is not naturally focusable and force is false', element);
    }

    return currentActiveElement;
};

export const getFirstDialogElementToFocus = (dialogContainer, focusStrategy = '') => {
    if (!dialogContainer) {
        console.warn('Dialog container is not a valid element', dialogContainer);
        return null;
    }

    // always check for long content first - prevent layout shifts takes priority
    if (checkForLongContentByHeight(dialogContainer)) {
      console.log('focusStrategy', `autodetect | ${FOCUS_STRATEGY_LONG_CONTENT}`);
      return getDialogTitle(dialogContainer);
    }

    // check for explicit focus strategy first
    let firstElementToFocus = getFirstElementToFocusByStrategy(
        dialogContainer,
        focusStrategy,
    );

    if (firstElementToFocus) {
        return firstElementToFocus;
    }

    // fallback: automatic detection for non-critical cases
    const hasFormElements = !!dialogContainer.querySelector('input, select, textarea, form');
    const hasCriticalActions = checkForCriticalActions(dialogContainer);
    const hasLegalContent = checkForLegalKeywords(dialogContainer);

    // form elements -> focus on first form element
    if (hasFormElements) {
        console.debug('focusStrategy', `autodetect | no critical actions and no form elements -> ${FOCUS_STRATEGY_FORM}`);
        firstElementToFocus = getFirstFormElement(dialogContainer);
    }

    // critial actions -> focus on safest action button
    if (hasCriticalActions) {
        console.debug('focusStrategy', `autodetect | critical action -> ${FOCUS_STRATEGY_CRITICAL_ACTION}`);
        firstElementToFocus = getSafestActionButton(dialogContainer);
    } else if (!hasFormElements) {
        console.debug('focusStrategy', `autodetect | no critical actions and no form elements -> ${FOCUS_STRATEGY_DISMISS}`);
        firstElementToFocus = getDismissButton(dialogContainer);
    }

    // legal content -> focus on title
    // user must read the content before they can continue
    if (hasLegalContent) {
        console.debug('focusStrategy', `autodetect -> ${FOCUS_STRATEGY_LEGAL_CONTENT}`);
        firstElementToFocus = getDialogTitle(dialogContainer);
    }

    // fallback to close button
    if (!firstElementToFocus) {
        console.debug('focusStrategy', `no element found -> ${FOCUS_STRATEGY_DISMISS}`);
        firstElementToFocus = getCloseButton(dialogContainer);
    }

    return firstElementToFocus;
};
